import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { mentorshipApi, showError, showSuccess } from "../lib";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, Star, Loader, Check } from "lucide-react";

const SessionReviewPage = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState("");

  useEffect(() => {
    if (sessionId) fetchSession();
  }, [sessionId]);

  const fetchSession = async () => {
    try {
      const response = await mentorshipApi.getSessionDetails(sessionId!);
      if (!response.success || response.data.status !== "completed") {
        showError("This session is not completed yet");
        navigate("/dashboard/student/mentorship-sessions");
      }
    } catch (err) {
      console.error("Failed to fetch session:", err);
      showError("Failed to load session");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (rating === 0) {
      showError("Please select a rating");
      return;
    }
    try {
      setSubmitting(true);
      const response = await mentorshipApi.createReview(sessionId!, rating, comment);
      if (response.success) {
        showSuccess("Review submitted!");
        navigate("/dashboard/student/mentorship-sessions");
      }
    } catch (err: any) {
      console.error("Failed to submit review:", err);
      showError(err.message || "Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <main className="min-h-screen pt-16 pb-16 bg-gray-50">
        <div className="flex items-center justify-center h-96">
          <Loader className="w-8 h-8 text-primary animate-spin" />
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen pt-16 pb-16 bg-gray-50">
      <div className="px-2 mx-auto max-w-md sm:px-4">
        {/* Header */}
        <div className="flex items-center gap-3 py-4">
          <button
            onClick={() => navigate("/dashboard/student/mentorship-sessions")}
            className="flex items-center justify-center w-8 h-8 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            <ArrowLeft className="w-4 h-4 text-gray-600" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-gray-900">Review Session</h1>
            <p className="text-xs text-gray-500">Share your experience</p>
          </div>
        </div>

        <div className="p-6 bg-white rounded-2xl border border-gray-200">
          <h2 className="text-base font-semibold text-gray-900 mb-6">How was your session?</h2>
          
          {/* Rating */}
          <div className="flex items-center justify-center gap-2 mb-8">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(0)}
                onClick={() => setRating(star)}
                className="p-1 transition-transform hover:scale-110"
              >
                <Star
                  className={`w-10 h-10 ${
                    star <= (hoverRating || rating)
                      ? "text-amber-400 fill-amber-400"
                      : "text-gray-300"
                  }`}
                />
              </button>
            ))}
          </div>
          <p className="text-center text-sm text-gray-500 mb-6">
            {rating === 0 ? "Tap to rate" : 
             rating === 1 ? "Poor" :
             rating === 2 ? "Fair" :
             rating === 3 ? "Good" :
             rating === 4 ? "Very Good" : "Excellent!"}
          </p>

          {/* Comment */}
          <div className="mb-6">
            <label className="block mb-2 text-sm font-medium text-gray-700">
              Your feedback (optional)
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="What did you learn? What could be improved?"
              rows={4}
              className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-primary resize-none"
            />
          </div>

          {/* Submit */}
          <button
            onClick={handleSubmit}
            disabled={submitting || rating === 0}
            className="w-full py-3 px-4 bg-primary text-white font-semibold rounded-xl hover:bg-primary/90 transition disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {submitting ? (
              <Loader className="w-4 h-4 animate-spin" />
            ) : (
              <Check className="w-4 h-4" />
            )}
            {submitting ? "Submitting..." : "Submit Review"}
          </button>
        </div>
      </div>
    </main>
  );
};

export default SessionReviewPage;